import { create } from "zustand";
import * as Models from "../Models";
import {
  SITE_TREE,
  PRODUCTS_TREE,
  CUSTOMERS_TREE,
  SITE_WITH_PARENTS,
} from "../Constants";
import axios from "../Api/index";
import * as fromAzureApi from "../Api/azure";
import { AxiosResponse } from "axios";
import * as _ from "lodash";
import * as Const from "../Constants";
import { history } from "..";
import { parse } from "query-string";
import * as StateHelper from "../Helpers/appState";
import { linkToDocumentLink, pushTo, scrollTop } from "../Helpers/links";
import { toggleItems } from "../Helpers/azure";
import { toast } from "react-toastify";
import salesInfoStore from "./SalesInfoStore";

type FilterItemsState = {
  allFilterItemIds: string[];
  sitesFilterItemIds: string[];
  customersFilterItemIds: string[];
  productFilterItemIds: string[];
  filterItemsById: any;
  filtersLoaded: boolean;
  searchItemIds: string[];
  projectAssetAttributeGroups: Models.AttributeGroup[];
  documentAttributeGroups: Models.AttributeGroup[];
  isCountrySearch: boolean;
  getDetails?: (
    url: string
  ) => Promise<Models.DetailBaseDataCollection | Models.DetailCollection>;
  setFiltersLoaded?: () => void;
  fetchAllData?: () => Promise<any>;
  getCountries?: () => void;
  getCustomers?: () => void;
  getProductTypes?: () => void;
  currentFilterItems?: (params: Models.UrlParams) => Models.Detail[];
  getActiveFilterItems?: (params: Models.UrlParams) => Models.Detail[];
  getActiveFilterItemsRootIds?: (params: Models.UrlParams) => string[];
  toggleItemSiblings?: (
    siblings: Models.SiblingsState,
    // navigationItemId: string,
    params: Models.UrlParams
  ) => void;
  navigationItemSelected?: (
    navigationItem: Models.DetailBaseData,
    params: Models.UrlParams,
    activate: boolean,
    isModalSelectionMode: boolean,
    shouldCloseMobileMenu?: boolean
  ) => void;
  openAllParentsForId?: (id: string) => void;
  setDetails?: (result: Models.DetailBaseDataCollection) => void;
  updateDetails?: (detailCollection: Models.DetailCollection) => void;
  replaceDetails?: (site: Models.DetailView) => void;
  getActiveDetail?: (params: Models.UrlParams) => Models.ActiveDetail;
  detailFilterItems?: (filter: string) => Models.Detail[];
  getAllCountryFilterItems?: (filter: string) => Models.Detail[];
  siblingsCanbeOpen?: (
    node: Models.DetailBaseData,
    params: Models.UrlParams
  ) => void;
  jumpToDetailsView?: (params: Models.UrlParams) => Promise<any>;
  treeNodeDetailsSelected?: (
    node: Models.DetailBaseData,
    activate: boolean,
    shouldCloseMobileMenu?: boolean
  ) => void;
  getActiveDetailBreadcrumb?: (
    params: Models.UrlParams
  ) => Models.DetailBaseData[];
  insertSearchIds?: (searchIds: string[]) => void;
  clearSearchIds?: () => void;
  updateProjectAssetAttributeGroups?: (
    attributeGroups: Models.AttributeGroup[]
  ) => void;
  getProjectAssetAttributeGroupById?: (Id: string) => Models.AttributeGroup;
  addProjectAssetAttributeGroups?: (
    addedRows: Models.AttributeRow[],
    alternativeProjectId: string
  ) => void;
  removeProjectAssetAttributeGroups?: (
    removedAssetId: string,
    projectId: string
  ) => void;
  updateDocumentAttributeGroup?: (
    attributeGroup: Models.AttributeGroup[]
  ) => void;
  removeDocumentAttributeGroup?: (
    attributeGroup: Models.AttributeGroup[]
  ) => void;
  getDocumentGroupByHostId?: (hostId: string) => Models.AttributeGroup;
  doSearch?: (
    userInput: string,
    suggestionFilter: string,
    params: Models.UrlParams,
    silent: boolean,
    isModalSearchMode?: boolean
  ) => void;
};

const initialState: FilterItemsState = {
  allFilterItemIds: [],
  sitesFilterItemIds: [],
  customersFilterItemIds: [],
  productFilterItemIds: [],
  filterItemsById: [],
  filtersLoaded: false,
  searchItemIds: [],
  projectAssetAttributeGroups: [],
  documentAttributeGroups: [],
  isCountrySearch: false,
};

const filterItemsStore = create<FilterItemsState>((set, get) => ({
  ...initialState,

  setFiltersLoaded: () => set({ filtersLoaded: true }),

  getDetails: async (url: string) => {
    try {
      const response: AxiosResponse = await axios.get(url);
      const result: Models.DetailBaseDataCollection | Models.DetailCollection =
        response.data;
      if ((result as Models.DetailBaseDataCollection).items !== undefined) {
        get().setDetails(result as Models.DetailBaseDataCollection);
      } else {
        const DetailCollection: Models.DetailCollection =
          result as Models.DetailCollection;
        get().updateDetails(DetailCollection);
        const site: Models.Detail = DetailCollection.sites[0];
        const siteId: string = site && site.id;
        if (
          DetailCollection.type === SITE_TREE ||
          DetailCollection.type === SITE_WITH_PARENTS
        ) {
          if (siteId) {
            const dataGroup: Models.DataGroup = _.find(
              site.dataGroups,
              (dataGroup: Models.DataGroup) =>
                dataGroup.name.toLocaleLowerCase() === "documents"
            );
            if (dataGroup) {
              const group: Models.AttributeGroup[] = _.filter(
                dataGroup.attributeGroups,
                (ag) => ag.rows.length > 0
              );
              const rows: Models.AttributeRow[] = _.flatten(
                _.map(group, (g) => g.rows)
              );
              const links: Models.Link[] = _.flatten(
                _.map(rows, (g) => g.items[1].url)
              );

              const documentLinks: Models.DocumentLink[] = links.map((l) =>
                linkToDocumentLink(l, siteId)
              );
              if (documentLinks.length > 0) {
                dataGroup.attributeGroups[0].siteId = siteId;
                get().updateDocumentAttributeGroup([
                  dataGroup.attributeGroups[0],
                ]);
              }
            }
          }
        }
      }
      return result;
    } catch (e) {
      toast.error("Could not fetch detail data");

      if (e.response && e.response.status === 403) {
        toast.error("This resource is not available.");
      }
    }
  },

  setDetails: (result: Models.DetailBaseDataCollection) => {
    set((state) => ({
      allFilterItemIds: _.union(
        state.allFilterItemIds.concat(result.items.map((c) => c.id))
      ),
      sitesFilterItemIds:
        result.type === SITE_TREE
          ? _.union(
              state.sitesFilterItemIds.concat(result.items.map((c) => c.id))
            )
          : state.sitesFilterItemIds,
      customersFilterItemIds:
        result.type === CUSTOMERS_TREE
          ? _.union(
              state.customersFilterItemIds.concat(result.items.map((c) => c.id))
            )
          : state.customersFilterItemIds,
      productFilterItemIds:
        result.type === PRODUCTS_TREE
          ? _.union(
              state.productFilterItemIds.concat(result.items.map((c) => c.id))
            )
          : state.productFilterItemIds,
      filterItemsById: _.merge(
        {},
        state.filterItemsById,
        _.keyBy(result.items, "id")
      ),
    }));
  },

  updateDetails: (detailCollection: Models.DetailCollection) => {
    let newSiteIds: string[] = [];
    detailCollection.sites.forEach((item: Models.DetailView) => {
      newSiteIds = newSiteIds
        .concat(item.id)
        .concat(
          _.map(item.childItems ? item.childItems : [], (child) => child.id)
        );
    });

    let newCustomerIds: string[] = [];
    detailCollection.customers.forEach((item: Models.DetailView) => {
      newCustomerIds = newCustomerIds.concat(item.id);

      if (!item.childItems) {
        return;
      }

      newCustomerIds = newCustomerIds.concat(
        _.map(item.childItems, (child) => child.id)
      );
    });

    let newProductIds: string[] = [];
    detailCollection.products.forEach((item: Models.DetailView) => {
      newProductIds = newProductIds.concat(item.id);

      if (!item.childItems) {
        return;
      }

      newProductIds = newProductIds.concat(
        _.map(item.childItems, (child) => child.id)
      );
    });

    let allDetails: Models.Detail[] = detailCollection.sites.concat(
      detailCollection.products.concat(detailCollection.customers)
    );

    if (detailCollection.type === "search") {
      const existingKeys: string[] = _.keys(get().filterItemsById);
      allDetails = _.filter(allDetails, (detail) => {
        if (existingKeys.indexOf(detail.id) === -1) {
          return true;
        }
        //added to fix search not working when we search asset second time after details view (check IB-2405)
        else {
          if (get().filterItemsById[detail.id].isDetailView) {
            return true;
          } else {
            return false;
          }
        }
      });
    }

    let childItemsList: Models.DetailBaseData[] = [];
    _.forEach(allDetails, (item) => {
      item.isDetailView =
        detailCollection.type && detailCollection.type === "search"
          ? false
          : true;

      if (!item.childItems) {
        return;
      }

      childItemsList = childItemsList.concat(item.childItems);
    });
    const children: _.Dictionary<Models.DetailBaseData> = _.keyBy(
      childItemsList,
      "id"
    );

    set((state) => ({
      sitesFilterItemIds: _.union(state.sitesFilterItemIds.concat(newSiteIds)),
      customersFilterItemIds: _.union(
        state.customersFilterItemIds.concat(newCustomerIds)
      ),
      productFilterItemIds: _.union(
        state.productFilterItemIds.concat(newProductIds)
      ),
      filterItemsById: _.merge(
        {},
        state.filterItemsById,
        children,
        _.keyBy(allDetails, "id")
      ),
    }));
  },

  replaceDetails: (site: Models.DetailView) => {
    const updatedFilterItemsById: Models.DetailView[] = _.map(
      get().filterItemsById,
      (detail: any) => {
        if (detail.id === site.id) {
          site.isDetailView = true;
          return site;
        }
        return detail;
      }
    );
    set({ filterItemsById: _.keyBy(updatedFilterItemsById, "id") });
  },

  getDocumentGroupByHostId: (hostId: string): Models.AttributeGroup => {
    if (!hostId) {
      return {
        rows: [],
        headers: [],
      } as Models.AttributeGroup;
    }
    return _.find(get().documentAttributeGroups, (g) => g.siteId === hostId);
  },

  currentFilterItems: (params: Models.UrlParams) => {
    let filter = params?.filter;
    if (filter === undefined) {
      filter = SITE_TREE;
    }

    let idsInFilter: string[] = [];

    if (filter === SITE_TREE) {
      idsInFilter = get().sitesFilterItemIds;
    } else if (filter === CUSTOMERS_TREE) {
      idsInFilter = get().customersFilterItemIds;
    } else if (filter === PRODUCTS_TREE) {
      idsInFilter = get().productFilterItemIds;
    }
    const filterItems: Models.Detail[] = idsInFilter.map(
      (id) => get().filterItemsById[id]
    );
    return filterItems;
  },
  getActiveFilterItems: (params: Models.UrlParams): Models.Detail[] => {
    return get()
      .currentFilterItems(params)
      .map((item: Models.Detail) => {
        return _.assign({}, item, {
          active:
            params.parameter === item.id ||
            params.activeFilterParameter === item.id,
        });
      });
  },

  /* Return all ids that should be shown on as root element */
  getActiveFilterItemsRootIds: (params: Models.UrlParams): string[] => {
    let filter = params?.filter;
    if (filter === undefined) {
      filter = SITE_TREE;
    }
    return filter === "search"
      ? get().searchItemIds
      : _.map(
          _.filter(
            get().getActiveFilterItems(params),
            (item: Models.DetailBaseData) =>
              item.parent === filter || item.type === "customers"
          ),
          (i) => i.id
        );
  },

  navigationItemSelected: (
    navigationItem: Models.DetailBaseData,
    params: Models.UrlParams,
    activate: boolean,
    isModalSelectionMode: boolean,
    shouldCloseMobileMenu?: boolean
  ) => {
    if (navigationItem.url === undefined || navigationItem.url === "") {
      return;
    }
    if (activate) {
      if (
        navigationItem.showChildren === undefined ||
        navigationItem.showChildren === false
      ) {
        get().siblingsCanbeOpen(navigationItem, params);
      }

      if (shouldCloseMobileMenu) {
        salesInfoStore.getState().mobileOverLayClose();
      }

      let queryParams: any = {};

      if (history.location.search) {
        const parsed: any = parse(history.location.search);

        if (Object.keys(parsed).length !== 0) {
          queryParams = parsed;
        }
      }

      const urlParams: Models.UrlParams = {
        filter: params.filter,
        itemType: navigationItem.type,
        parameter: navigationItem.id,
        queryParams,
      };

      if (StateHelper.isNavigationItemHidden(navigationItem)) {
        urlParams.activeFilterParameter = navigationItem.parent;
      }

      if (
        params.filter === Const.PRODUCTS_TREE &&
        navigationItem.parent === Const.PRODUCTS_TREE
      ) {
        urlParams.itemType = "productType";
      }

      if (!isModalSelectionMode) {
        pushTo(urlParams);
      }

      if (params.filter !== Const.CUSTOMERS_TREE) {
        const newFilterItemsbyId = toggleItems(
          get().filterItemsById,
          [navigationItem.id],
          undefined,
          true
        );
        set({ filterItemsById: newFilterItemsbyId });
      }
    }
  },

  siblingsCanbeOpen: (
    node: Models.DetailBaseData,
    params: Models.UrlParams
  ) => {
    if (
      node.type === Const.TYPE_SITE ||
      node.type === Const.TYPE_COUNTRY ||
      node.parent === Const.PRODUCTS_TREE
    ) {
      const siblingState: Models.SiblingsState = {
        node,
        open: false,
      };
      get().toggleItemSiblings(siblingState, params);
    }
  },

  toggleItemSiblings: (
    siblings: Models.SiblingsState,
    params: Models.UrlParams
  ) => {
    const activeFilterItems = get().getActiveFilterItems(params); // this can be calculated inside if for optimization
    if (siblings.node === undefined) {
      siblings.node = _.find(
        activeFilterItems,
        (item: Models.DetailBaseData) => item.id === params.parameter
      );
      if (siblings.node === undefined) {
        console.log("Error case: could not find item");
        return;
      }
    }
    const parentId: string = siblings.node.parent;

    if (siblings.open) {
      if (
        parentId === Const.SITE_TREE ||
        parentId === Const.PRODUCTS_TREE ||
        siblings.node.type === Const.TYPE_SITE
      ) {
        return;
      }

      activeFilterItems.forEach((item: Models.DetailBaseData) => {
        if (
          item.parent === parentId &&
          (item as Models.Detail).dataGroups === undefined
        ) {
          const sibling: any = _.find(
            activeFilterItems,
            (i: Models.DetailBaseData) => i.id === item.id
          );

          if (sibling.dataGroups === undefined) {
            get().getDetails(
              fromAzureApi.getItemDetail({
                filter: params.filter,
                itemType: sibling.type,
                parameter: sibling.id,
              })
            );
          }
          get().navigationItemSelected(item, { filter: "sites" }, false, false);
        }
      });
    }

    const items: Models.DetailBaseData[] = Object.keys(
      get().filterItemsById
    ).map((key: string, index: Number) => {
      const item: Models.DetailBaseData = get().filterItemsById[key];

      if (item.parent === siblings.node.parent) {
        item.showChildren = siblings.open;
      }

      return item;
    });
    set({ filterItemsById: _.keyBy(items, "id") });
  },

  openAllParentsForId: (id: string) => {
    //params was also added earlier in parameters earlier but removed it as it was unused, can be added later if required
    let ids: string[] = [];
    let hasParents: boolean = true;

    const newItems: Models.DetailBaseData[] = Object.keys(
      get().filterItemsById
    ).map((key: string, index: Number) => {
      const item: Models.DetailBaseData = get().filterItemsById[key];

      if (item.showChildren) {
        item.showChildren = false;
      }

      return item;
    });
    set({ filterItemsById: _.keyBy(newItems, "id") });

    while (hasParents) {
      let item: Models.DetailBaseData = _.find(
        get().filterItemsById,
        (i: Models.DetailBaseData) => i.id === id
      );
      if (item === undefined) {
        break;
      }

      ids.push(item.id);

      if (item && item.parent !== undefined && item.parent !== "") {
        id = item.parent;
      } else {
        ids.push(item.type);
        hasParents = false;
      }
    }

    const items: Models.SetOpenState = { parents: ids, newState: true };
    const updatedItems = toggleItems(
      get().filterItemsById,
      items.parents,
      true
    );
    set({ filterItemsById: updatedItems });
  },

  getActiveDetail: (params: Models.UrlParams): Models.ActiveDetail => {
    if (params === undefined) {
      return undefined;
    }
    let detail: Models.DetailView;
    let activeId: string = "";

    if (params.filter === "search" && params.parameter === undefined) {
      /* If there is only one search result in filter and no active detail then return only result */
      let searchResultsInFilter: any[];
      if (params.filter === SITE_TREE) {
        searchResultsInFilter = _.intersection(
          get().sitesFilterItemIds,
          get().searchItemIds
        );
      } else if (params.filter === CUSTOMERS_TREE) {
        searchResultsInFilter = _.intersection(
          get().customersFilterItemIds,
          get().searchItemIds
        );
      } else if (params.filter === PRODUCTS_TREE) {
        searchResultsInFilter = _.intersection(
          get().productFilterItemIds,
          get().searchItemIds
        );
      }
      if (searchResultsInFilter.length !== 1) {
        return undefined;
      }
      activeId = searchResultsInFilter[0];
    } else {
      activeId = params.parameter;
    }

    detail = _.find(
      get().filterItemsById,
      (detail: Models.DetailView) => detail.id === activeId
    );

    if (detail && detail.type === "country") {
      return {
        detail,
        isProductDetail: false,
        isSiteDetail: false,
        isCustomer: false,
        isLinks: false,
        documentType: "sites",
      };
    }

    if (detail === undefined || detail.dataGroups === undefined) {
      return undefined;
    } // check if active item has details view
    const isSiteDetail: boolean = _.find(
      get().sitesFilterItemIds,
      (id: string) => detail.id === id
    )
      ? true
      : false;
    const isCustomer: boolean = _.find(
      get().customersFilterItemIds,
      (id: string) => detail.id === id
    )
      ? true
      : false;
    const isProductDetail: boolean = _.find(
      get().productFilterItemIds,
      (id: string) => detail.id === id
    )
      ? true
      : false;

    const activeDetail: Models.ActiveDetail = {
      detail,
      isProductDetail,
      isCustomer,
      isSiteDetail,
      isLinks: detail.linkGroups?.length > 0,
      documentType: isProductDetail ? "products" : isSiteDetail ? "sites" : "",
    };

    return activeDetail;
  },

  detailFilterItems: (filter: string) => {
    if (filter === undefined) return;
    if (filter === SITE_TREE) {
      return get()
        .sitesFilterItemIds.map((id) => get().filterItemsById[id])
        .filter((f) => (f as Models.Detail).childItems !== undefined);
    } else if (filter === PRODUCTS_TREE) {
      return get()
        .productFilterItemIds.map((id) => get().filterItemsById[id])
        .filter((f) => (f as Models.Detail).childItems !== undefined);
    }
  },

  getAllCountryFilterItems: (filter: string) => {
    if (filter === undefined) return;
    if (filter === SITE_TREE) {
      return get()
        .sitesFilterItemIds.map((id) => get().filterItemsById[id])
        .filter((f) => (f as Models.DetailBaseData).type === "country");
    } else if (filter === PRODUCTS_TREE) {
      return get()
        .productFilterItemIds.map((id) => get().filterItemsById[id])
        .filter((f) => (f as Models.DetailBaseData).type === "country");
    }
  },

  jumpToDetailsView: async (params: Models.UrlParams) => {
    scrollTop();
    const { parameter, filter, itemType } = params;
    let detail: Models.Detail = get()
      .getActiveFilterItems(params)
      .find((f) => f.id === parameter);
    if (!detail || detail.header === undefined) {
      const treeItems: Models.DetailView[] =
        await fromAzureApi.getItemAndParents(
          params,
          get().setDetails,
          get().updateDetails,
          get().updateProjectAssetAttributeGroups,
          get().updateDocumentAttributeGroup
        );

      if (treeItems === undefined || treeItems.length === 0) {
        return;
      }
      detail = treeItems[0];
    }
    let rootIdToCloseSiblings: string =
      (detail as any)?.root === "" ? detail.parent : (detail as any)?.root;

    let parent: Models.DetailBaseData = _.find(
      get().currentFilterItems(params),
      (item: Models.DetailBaseData) => item.id === rootIdToCloseSiblings
    );
    if (parent) {
      get().siblingsCanbeOpen(parent, { parameter: (detail as any).id });
      get().treeNodeDetailsSelected(parent as Models.DetailView, false);
    }

    get().treeNodeDetailsSelected(detail as Models.DetailView, true);
    setTimeout(() => {
      get().openAllParentsForId(detail?.id);
    }, 250);
  },

  treeNodeDetailsSelected: (
    node: Models.DetailBaseData,
    activate: boolean,
    shouldCloseMobileMenu?: boolean
  ) => {
    if (node.showChildren === false) {
      get().siblingsCanbeOpen(node, { parameter: node.id });
    }

    toggleItems(get().filterItemsById, [node.id], undefined, true);

    if (activate) {
      var belongsToFilter: string = "";
      const isCustomer =
        _.find(
          get().customersFilterItemIds,
          (customerId: string) => node.id === customerId
        ) !== undefined;

      const isProduct =
        _.find(
          get().productFilterItemIds,
          (productId: string) => node.id === productId
        ) !== undefined;
      if (isCustomer) {
        belongsToFilter = Const.CUSTOMERS_TREE;
      } else if (isProduct) {
        belongsToFilter = Const.PRODUCTS_TREE;
      } else {
        belongsToFilter = Const.SITE_TREE;
      }
      const queryParams: any = parse(history.location.search);

      const urlParams: Models.UrlParams = {
        filter: belongsToFilter,
        itemType: node.type,
        parameter: node.id,
        queryParams,
      };

      pushTo(urlParams, true);
    }

    if (shouldCloseMobileMenu) {
      salesInfoStore.getState().mobileOverLayClose();
    }
  },

  getActiveDetailBreadcrumb: (
    params: Models.UrlParams
  ): Models.DetailBaseData[] => {
    const active: Models.ActiveDetail = get().getActiveDetail(params);
    if (active === undefined) {
      return undefined;
    }
    const activeDetailId = active.detail?.id;
    if (activeDetailId === undefined) {
      return [];
    }

    const hierarchy: Models.DetailBaseData[] = [];

    const travel: Function = (currentId: string) => {
      const current: Models.DetailBaseData = get().filterItemsById[currentId];
      if (current) {
        hierarchy.push(current);

        if (current.parent !== "") {
          travel(current.parent);
        }
      }
    };

    travel(activeDetailId);

    return hierarchy.reverse();
  },

  insertSearchIds: (searchIds: string[]) => {
    set({ searchItemIds: searchIds });
  },

  clearSearchIds: () => {
    set({ searchItemIds: [] });
  },

  updateProjectAssetAttributeGroups: (
    attributeGroups: Models.AttributeGroup[]
  ) => {
    const groups: Models.AttributeGroup[] =
      get().projectAssetAttributeGroups?.filter(
        (d) => d.siteId !== attributeGroups[0].siteId
      );

    const assetgroup = groups.concat(attributeGroups);
    set({ projectAssetAttributeGroups: assetgroup });
  },

  addProjectAssetAttributeGroups: (
    addedRows: Models.AttributeRow[],
    alternativeProjectId: string
  ) => {
    const assetAttributeGroup =
      get().getProjectAssetAttributeGroupById(alternativeProjectId);
    const updatedAssetAttributeGroup: Models.AttributeGroup = {
      ...assetAttributeGroup,
      rows: [...assetAttributeGroup.rows, ...addedRows],
    };
    set({ projectAssetAttributeGroups: [updatedAssetAttributeGroup] });
  },

  removeProjectAssetAttributeGroups: (
    removedAssetId: string,
    projectId: string
  ) => {
    const assetAttributeGroup =
      get().getProjectAssetAttributeGroupById(projectId);
    const updatedRows: Models.AttributeRow[] = assetAttributeGroup.rows.filter(
      (row) =>
        !row.items.some((item) => item.url?.description === removedAssetId)
    );
    const updatedAssetAttributeGroup: Models.AttributeGroup = {
      ...assetAttributeGroup,
      rows: updatedRows,
    };

    const updatedAssetAttributeGroups = get().projectAssetAttributeGroups.map(
      (group) => {
        if (group.siteId === updatedAssetAttributeGroup.siteId) {
          // Return the updated group if the siteId matches
          return { ...group, ...updatedAssetAttributeGroup }; // Merge the updated group
        }
        return group; // Otherwise, return the group unchanged
      }
    );
    set({ projectAssetAttributeGroups: updatedAssetAttributeGroups });
  },

  getProjectAssetAttributeGroupById: (Id: string) => {
    if (!Id) {
      return {
        rows: [],
        headers: [],
      } as Models.AttributeGroup;
    }
    return get().projectAssetAttributeGroups?.find((g) => g.siteId === Id);
  },

  updateDocumentAttributeGroup: (attributeGroup: Models.AttributeGroup[]) => {
    const groups: Models.AttributeGroup[] =
      get().documentAttributeGroups.filter(
        (D) => D.siteId !== attributeGroup[0].siteId
      );
    set({
      documentAttributeGroups: [...groups, ...attributeGroup],
    });
  },

  removeDocumentAttributeGroup: (attributeGroup: Models.AttributeGroup[]) => {
    const updateGroup = get().documentAttributeGroups.map((i) => {
      if (i.siteId === attributeGroup[0].siteId) {
        return attributeGroup[0];
      }
      return i;
    });
    set({ documentAttributeGroups: updateGroup });
  },

  doSearch: async (
    userInput: string,
    suggestionFilter: string,
    params: Models.UrlParams,
    silent: boolean,
    isModalSearchMode?: boolean
  ) => {
    if (userInput !== "") {
      const response: AxiosResponse = await fromAzureApi.searchTree(
        userInput,
        suggestionFilter,
        params,
        silent,
        isModalSearchMode
      );

      let searchResults: Models.SearchResults;
      let searchIds: string[];
      if (response.status === 200) {
        searchResults = response.data;
        searchResults.sites = [...searchResults.locations];
        if (!searchResults.isCountrySearch) {
          searchResults.sites = [
            ...searchResults.sites,
            ...searchResults.builds,
          ];
        }
        set({ isCountrySearch: !!searchResults.isCountrySearch });
        get().updateDetails(searchResults);
        let allSearchResults: Models.DetailBaseData[] = _.flatten(
          _.filter(_.values(searchResults), (v: any) => _.isArray(v))
        );

        searchIds = _.map(
          allSearchResults,
          (result: Models.DetailBaseData) => result.id
        );

        const siteCount: number = _.filter(
          searchResults.sites,
          (l) => l.type === "Site"
        ).length;

        get().insertSearchIds(searchIds);
        if (
          siteCount === 1 &&
          suggestionFilter === SITE_TREE &&
          !isModalSearchMode
        ) {
          //   dispatch(SalesInfoActions.toggleMobileSearch());
          salesInfoStore.getState().toggleMobileSearch();
          const location: Models.DetailBaseData = _.find(
            searchResults.sites,
            (location) => location.type === Const.TYPE_SITE
          );
          params.filter = "sites";
          params.parameter = location.id;
          params.itemType = location.type;
          if (!silent) {
            params.queryParams = { search: userInput };
          }
          if (!isModalSearchMode) {
            pushTo(params);
          }
          return undefined;
        } else if (
          _.uniqBy(searchResults.builds, (b) => b.id).length === 1 &&
          suggestionFilter === SITE_TREE &&
          !isModalSearchMode
        ) {
          //   dispatch(SalesInfoActions.toggleMobileSearch());
          salesInfoStore.getState().toggleMobileSearch();
          const build: Models.DetailBaseData = searchResults.builds[0];
          params.filter = "sites";
          params.parameter = build.id;
          params.itemType = "build";
          params.queryParams = {};
          if (!isModalSearchMode) {
            pushTo(params);
          }
          return undefined;
        } else if (
          _.uniqBy(searchResults.customers, (b) => b.id).length === 1 &&
          suggestionFilter === CUSTOMERS_TREE &&
          !isModalSearchMode
        ) {
          salesInfoStore.getState().toggleMobileSearch();
          const customer: Models.DetailBaseData = searchResults.customers[0];
          params.filter = "customers";
          params.parameter = customer.id;
          params.itemType = customer.type;
          params.queryParams = { search: userInput };
          if (!isModalSearchMode) {
            pushTo(params);
          }
          return undefined;
        } else {
          salesInfoStore.getState().toggleMobileMenu();
        }
      } else if (response.status === 204) {
        searchIds = [];
        get().insertSearchIds(searchIds);
      }
    }
  },
}));

export default filterItemsStore;
